import type { ReactNode } from 'react';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import { variantByIndex } from '@/helpers/utils';
import AiLogo from '@/assets/img/artificial-intelligence.svg?react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import { useDispatch } from 'react-redux';
import { showgapReportModal } from '@/app/KeywordResearch/reducers/gapReportModal';
import { Table } from 'react-bootstrap';
import Divider from '@/components/common/Divider';
import classNames from 'classnames';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';

type IntentKey = 'I' | 'N' | 'T' | 'C';

type IntentConfig = {
  tooltip: ReactNode;
  variant: string;
};

type SubmittedHost = {
  host: string;
  i: number;
};

type KeywordGapRow = {
  keyword: string;
  intent: IntentKey[];
  purpose: string[];
  difficulty?: number;
  keyword_search_volume?: number;
  [host: string]: string | number | string[] | undefined;
};

type KeywordGapColumn = ColDef<KeywordGapRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  icon?: ReactNode;
  tooltip?: ReactNode;
};

export const purposeMap: Record<string, string> = {
  Grouping: 'دسته‌بندی',
  local: 'محلی',
  Product: 'محصول',
  'Tag or Filter': 'برچسب یا فیلتر',
  Brand: 'برند',
  'Brand Page': 'برند',
  Article: 'مقاله',
  Service: 'خدمات',
  'Service Page': 'خدمات',
  'Site Overview': 'سایر'
};

export const intentMap: Record<IntentKey, IntentConfig> = {
  I: {
    tooltip: (
      <div className="text-start">
        Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره یک موضوع خاص
        است. مثال: "چگونه یک ماشین دنده اتومات تعمیر کنیم؟" "فواید ورزش برای
        سلامتی."
      </div>
    ),
    variant: 'info'
  },
  N: {
    tooltip: (
      <div className="text-start">
        Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
        موضوع خاص است. مثال: جستجوی "Instagram" برای رفتن به وب‌سایت اینستاگرام.
        جستجو درباره موضوعی خاص مثل "وردپرس".
      </div>
    ),
    variant: 'danger'
  },
  T: {
    tooltip: (
      <div className="text-start">
        Transactional (هدف تراکنشی یا تجاری):
        <br /> کاربر قصد خرید یا انجام یک اقدام خاص را دارد. مثال: "خرید لپ‌تاپ
        اپل." "رزرو هتل در کیش."
      </div>
    ),
    variant: 'success'
  },
  C: {
    tooltip: (
      <div className="text-start">
        Commercial (تحقیق تجاری): این نوع جستجو ترکیبی است و کاربر هنوز تصمیم
        نگرفته است، اما در حال بررسی گزینه‌ها است. مثال: "بهترین گوشی‌های 2025."
        "مقایسه پژو 206 و تیبا."
      </div>
    ),
    variant: 'warning'
  }
};

export const keywordGapColumnConfig = (
  columns: string[] = [],
  submittedHosts: SubmittedHost[] = []
): KeywordGapColumn[] | undefined => {
  if (columns) {
    const mapColumns: KeywordGapColumn[] = [
      {
        field: 'keyword',
        suppressHeaderMenuButton: true,
        type: 'string',
        title: 'کلمه',
        minWidth: 340,
        flex: 1,
        searchable: true,
        cellRenderer: (params: ICellRendererParams<KeywordGapRow>) => {
          let keywordDecoded: string;
          try {
            keywordDecoded = decodeURIComponent(params.value);
          } catch {
            keywordDecoded = params.value;
          }
          return (
            <div className="text-nowrap d-flex aligns-items-center gap-2">
              <span
                style={{
                  fontFeatureSettings: '"tnum" 0',
                  fontVariantNumeric: 'normal',
                  unicodeBidi: 'plaintext'
                }}
                className="mx-1"
              >
                {keywordDecoded}
              </span>
            </div>
          );
        }
      }
    ];

    columns.map(item => {
      const index = submittedHosts?.find(({ host }) => host === item)?.i;
      mapColumns.push({
        icon: (
          <FontAwesomeIcon
            style={{ marginBottom: -2, fontSize: 9 }}
            className={`text-${variantByIndex(index)}`}
            icon="circle"
          />
        ),
        field: decodeURIComponent(item),
        suppressHeaderMenuButton: true,
        title: decodeURIComponent(item),
        type: 'number',
        width: 120
      });
    });

    mapColumns.push(
      {
        field: 'difficulty',
        suppressHeaderMenuButton: true,
        type: 'number',
        title: 'سختی ',
        tooltip:
          'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود. سختی بیشتر یعنی نیاز به زمان، هزینه و تکنیک‌های پیشرفته‌تر برای رسیدن به رتبه‌های برتر.',
        width: 90,
        searchable: true,
        cellRenderer: (params: ICellRendererParams<KeywordGapRow>) =>
          params.value && (
            <div className="d-flex align-items-center justify-content-center">
              <ChildrensTooltip
                placement="top"
                html={
                  <div>
                    سختی کلمه کلیدی
                    <Divider />
                    <Table borderless size="sm">
                      <tbody>
                        {difficulyRanges.map(([start, end]) => (
                          <tr
                            className={classNames({
                              'fw-black fs--1 position-relative':
                                serpDifficuly(start).color ===
                                serpDifficuly(params.value).color
                            })}
                            key={start}
                          >
                            <td className="d-flex  align-items-center gap-1">
                              {serpDifficuly(start).color ===
                                serpDifficuly(params.value).color && (
                                <FontAwesomeIcon
                                  style={{
                                    right: 3,
                                    top: '50%',
                                    transform: 'translateY(-50%)'
                                  }}
                                  icon="chevron-left"
                                  className="position-absolute text-primary"
                                  size="sm"
                                />
                              )}
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  direction: 'ltr',
                                  height: '20px',
                                  width: '28px',
                                  fontSize: '12px',
                                  backgroundColor: serpDifficuly(start).color
                                }}
                                className="fs--2 px-2 py-0.5 text-white rounded-2"
                              >
                                {start}
                              </span>
                              <FontAwesomeIcon icon="minus" />
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  direction: 'ltr',
                                  height: '20px',
                                  width: '28px',
                                  fontSize: '12px',
                                  backgroundColor: serpDifficuly(start).color
                                }}
                                className="fs--2 px-2 py-0.5 text-white rounded-2"
                              >
                                {end}
                              </span>
                            </td>
                            <td className="text-white">
                              {serpDifficuly(start).label}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                }
              >
                <div
                  className="fs-1 rounded-2 w-full"
                  style={{
                    fontWeight: 'bold'
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      direction: 'ltr',
                      marginTop: '6px',
                      height: '20px',
                      width: '28px',
                      fontSize: '12px',
                      backgroundColor: serpDifficuly(params.value).color
                    }}
                    className="fs--2 px-2 py-0.5 text-white rounded-2"
                  >
                    {params.value}
                  </span>
                </div>
              </ChildrensTooltip>
            </div>
          )
      },
      {
        field: 'intent',
        suppressHeaderMenuButton: true,
        type: 'select',
        icon: (
          <AiLogo
            style={{
              width: 13,
              marginTop: 6,
              fill: 'var(--ag-header-foreground-color)'
            }}
          />
        ),
        title: 'نیت',
        width: 90,
        searchable: true,
        cellRenderer: (params: ICellRendererParams<KeywordGapRow>) => {
          return (
            <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
              {params.value?.map((intent: IntentKey, i: number) => (
                <ChildrensTooltip
                  placement="top"
                  key={i}
                  delayToRemove={0}
                  html={intentMap[intent]?.tooltip}
                >
                  <SubtleBadge
                    pill
                    key={i}
                    bg={intentMap[intent]?.variant}
                    className="d-flex align-items-center gap-1  fs--2"
                  >
                    {intent}
                  </SubtleBadge>
                </ChildrensTooltip>
              ))}
              <ReportButton params={params} />
            </div>
          );
        },
        tooltip: (
          <div className="text-start">
            نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی
            چیه؟ چهار نوع اصلی داره:
            <br /> Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره
            یک موضوع خاص است. مثال: “چگونه یک ماشین دنده اتومات تعمیر کنیم؟”
            “فواید ورزش برای سلامتی.” این کلمات برای نوشتن مقالات مناسبن. <br />
            Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
            موضوع خاص است. مثال: خرید عینک آفتابی در دیجیکالا.
            <br />
            Commercial (تحقیق تجاری): این نوع جستجو ترکیبیه و کاربر هنوز تصمیم
            نگرفته، اما در حال بررسی و مقایسه گزینه‌هاست. مثال: “بهترین گوشی‌های
            2025.” “مقایسه پژو 206 و تیبا.” <br />
            Transactional (هدف تراکنشی یا تجاری): کاربر قصد خرید یا انجام یک
            اقدام خاص را دارد. مثال: “خرید لپ‌تاپ اپل.” “رزرو هتل در کیش.” داده
            های ستون نیت از هوش مصنوعی گرفته میشه.
          </div>
        )
      },
      {
        field: 'purpose',
        suppressHeaderMenuButton: true,
        type: 'select',
        width: 90,
        title: 'هدف',
        icon: (
          <AiLogo
            style={{
              width: 13,
              marginTop: 6,
              fill: 'var(--ag-header-foreground-color)'
            }}
          />
        ),
        searchable: true,
        cellRenderer: (params: ICellRendererParams<KeywordGapRow>) => {
          return (
            <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
              {params.value?.map((purpose: string, i: number) => (
                <SubtleBadge
                  pill
                  key={i}
                  bg="info"
                  className="d-flex align-items-center gap-1  fs--2"
                >
                  {purposeMap[purpose]}
                </SubtleBadge>
              ))}
              <ReportButton params={params} />
            </div>
          );
        },
        tooltip: (
          <>
            پیشنهاد میده که کلمه برای چه نوع محتوایی مناسب‌تره. مثلاً برای
            <strong>&nbsp;دسته‌بندی </strong>سایت، <strong>&nbsp;مقاله</strong>،
            یا
            <strong>&nbsp;برند</strong> مفیده. با استفاده از این فیلتر می‌تونید
            کلمات کلیدی رو بر اساس هدف خاص خودتون (مثل ایجاد محتوای آموزشی،
            تبلیغاتی یا برندینگ) انتخاب کنید. داده های این بخش از هوش مصنوعی
            گرفته میشه.
          </>
        )
      },
      {
        field: 'keyword_search_volume',
        suppressHeaderMenuButton: true,
        type: 'number',
        title: 'میزان جستجو',
        width: 130,
        searchable: true
      }
    );
    return mapColumns;
  }
};

type ReportButtonProps = {
  params: ICellRendererParams<KeywordGapRow>;
};

export const ReportButton = ({ params }: ReportButtonProps) => {
  const dispatch = useDispatch();
  return (
    <ChildrensTooltip placement="top" delayToRemove={0} html="گزارش پیشنهاد">
      <div className="cursor-pointer" id="FlagMenu">
        <FontAwesomeIcon
          onClick={() =>
            dispatch(
              showgapReportModal({
                keywords: params.data?.keyword,
                intent: params.data?.intent,
                purpose: params.data?.purpose
              })
            )
          }
          className="little-icon"
          icon="flag"
        />
      </div>
      <style>{`
#FlagMenu {
display:none !important;
}
.ag-row-hover.ag-row-level-0 #FlagMenu {
display: initial !important;
background-color: var(--ag-column-hover-color)!important;
}
//TODO ag-cell-range-selected ag-cell-range-selected-1 ag-cell-range-top ag-cell-range-right
.ag-row-selected.ag-row-level-0 #FlagMenu {
display: none !important;
background-color:var(--ag-selected-row-background-color);
}
`}</style>
    </ChildrensTooltip>
  );
};
